import { FormControlLabel } from '@mui/material';
import type { FC, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import { Hotkey } from 'config/enums';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { useCallHearingParticipantHandlers } from 'features/call/call-accepted/hooks';
import { selectIsCallCanBeClosed } from 'features/call/call-finish/store';
import {
  primaryHearingPhoneNumberSelector,
  selectIsAnyHearingConnecting,
  selectIsLoadingPrimaryHearing,
  selectIsLoadingVco2LineHearing,
} from 'features/call/call-hearing/store';
import {
  deafPhoneNumberIsHiddenSelector,
  toggleDeafPhoneNumberIsHiddenState,
} from 'features/deaf-phone-number-hidden/store';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';
import { Button, Switch } from 'features/ui/elements';

export const DeafPhoneNumberHidden: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { handleRegularHearingCall } = useCallHearingParticipantHandlers();
  const isLoadingPrimaryHearing = useAppSelector(selectIsLoadingPrimaryHearing);
  const deafPhoneNumberIsHidden = useAppSelector(
    deafPhoneNumberIsHiddenSelector
  );
  const primaryHearingPhoneNumber = useAppSelector(
    primaryHearingPhoneNumberSelector
  );
  const { canBeClosed } = useAppSelector(selectIsCallCanBeClosed);
  const isAnyHearingConnecting = useAppSelector(selectIsAnyHearingConnecting);
  const isLoadingVco2LineHearing = useAppSelector(
    selectIsLoadingVco2LineHearing
  );

  return (
    <>
      <StyledFormControlLabel
        control={
          <Switch
            onChange={() => dispatch(toggleDeafPhoneNumberIsHiddenState())}
            checked={!deafPhoneNumberIsHidden}
          />
        }
        label={t('showCallerId')}
      />

      <Button
        fullWidth
        size='small'
        color='primary'
        loading={isLoadingPrimaryHearing}
        onClick={() => handleRegularHearingCall(primaryHearingPhoneNumber)}
        hotkey={Hotkey.CalleeConnectionStateChange}
        disabled={
          canBeClosed || isLoadingVco2LineHearing || isAnyHearingConnecting
        }
      >
        {t('connect')}
      </Button>
      {children}
    </>
  );
};

const StyledFormControlLabel = styled(FormControlLabel)`
  margin-bottom: ${theme.spacing(8)};
`;
